import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Modal from './Modal';
import { PieChart, LineChart } from './Charts';
import { formatDate } from '../utils/dateFormatter';
import { AdminLogAiSearch } from './AdminLogAiSearch';
import { RefreshCw, Calendar, X, ChevronDown, ChevronRight } from 'lucide-react';
import './AdminLog.css';

type ChangeType = 'Google' | 'Meta' | 'Klaviyo' | 'Fabricacion' | 'Lanzamiento' | 'Promo' | 'Otros';

interface AdminLog {
  id: number;
  change_type: ChangeType;
  change_date: string;
  title: string;
  description: string | null;
  images?: string[];
  created_by: number | null;
  created_at: string;
  updated_at: string;
  username?: string;
}

interface LogFormData {
  change_type: ChangeType;
  change_date: string;
  title: string;
  description: string;
  images: File[];
  existingImages?: string[]; // URLs de imágenes ya subidas (para edición)
}

const CHANGE_TYPE_COLORS: Record<ChangeType, string> = {
  Google: '#4285F4',
  Meta: '#1877F2',
  Klaviyo: '#FF6B6B',
  Fabricacion: '#008060',
  Lanzamiento: '#F49342',
  Promo: '#9B59B6',
  Otros: '#95A5A6',
};

const CHANGE_TYPE_ICONS: Record<ChangeType, string> = {
  Google: '🔵',
  Meta: '🔷',
  Klaviyo: '📧',
  Fabricacion: '⚙️',
  Lanzamiento: '🚀',
  Promo: '🎁',
  Otros: '📝',
};

export default function AdminLog() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { isAdmin } = useAuth();

  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AdminLog | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [statsByType, setStatsByType] = useState<Array<{ change_type: string; count: number }>>([]);
  const [statsByDate, setStatsByDate] = useState<Array<{ date: string; count: number }>>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Filtros
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [filterType, setFilterType] = useState<ChangeType | 'all'>('all');

  // Formulario
  const [formData, setFormData] = useState<LogFormData>({
    change_type: 'Google',
    change_date: new Date().toISOString().split('T')[0],
    title: '',
    description: '',
    images: [],
    existingImages: []
  });

  useEffect(() => {
    if (isAdmin) {
      loadLogs();
      loadStats();
    }
  }, [isAdmin, startDate, endDate, filterType]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params: any = {
        start_date: startDate,
        end_date: endDate,
      };

      if (filterType !== 'all') {
        params.change_type = filterType;
      }

      const response = await api.get('/admin-logs', { params });
      setLogs(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar logs');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const [typeStats, dateStats] = await Promise.all([
        api.get('/admin-logs/stats/type', {
          params: { start_date: startDate, end_date: endDate },
        }),
        api.get('/admin-logs/stats/date', {
          params: { start_date: startDate, end_date: endDate },
        }),
      ]);

      setStatsByType(typeStats.data);
      setStatsByDate(dateStats.data);
    } catch (err: any) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  // Manejo de pegado de imágenes
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    const newFiles: File[] = [];

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          newFiles.push(file);
        }
      }
    }

    if (newFiles.length > 0) {
      e.preventDefault();
      const totalImages = formData.images.length + formData.existingImages!.length + newFiles.length;
      if (totalImages > 10) {
        showError(`Máximo 10 imágenes permitidas. Ya tienes ${formData.images.length + formData.existingImages!.length}`);
        return;
      }
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newFiles].slice(0, 10 - (prev.existingImages?.length || 0))
      }));
      showSuccess(`${newFiles.length} imagen(es) pegada(s)`);
    }
  };

  // Manejo de drop de archivos
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
      
      if (newFiles.length > 0) {
        const totalImages = formData.images.length + formData.existingImages!.length + newFiles.length;
        if (totalImages > 10) {
          showError(`Máximo 10 imágenes permitidas. Ya tienes ${formData.images.length + formData.existingImages!.length}`);
          return;
        }
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newFiles].slice(0, 10 - (prev.existingImages?.length || 0))
        }));
        showSuccess(`${newFiles.length} imagen(es) agregada(s)`);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemoveImage = (index: number, isExisting: boolean) => {
    if (isExisting) {
      setFormData(prev => ({
        ...prev,
        existingImages: prev.existingImages?.filter((_, i) => i !== index) || []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files).filter(file => file.type.startsWith('image/'));
      if (newFiles.length > 0) {
        const totalImages = formData.images.length + formData.existingImages!.length + newFiles.length;
        if (totalImages > 10) {
          showError(`Máximo 10 imágenes permitidas. Ya tienes ${formData.images.length + formData.existingImages!.length}`);
          return;
        }
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newFiles].slice(0, 10 - (prev.existingImages?.length || 0))
        }));
        showSuccess(`${newFiles.length} imagen(es) seleccionada(s)`);
      }
    }
    // Reset input para permitir seleccionar el mismo archivo de nuevo
    e.target.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showError('El título es obligatorio');
      return;
    }

    try {
      const data = new FormData();
      data.append('change_type', formData.change_type);
      data.append('change_date', formData.change_date);
      data.append('title', formData.title);
      data.append('description', formData.description);
      
      // Adjuntar imágenes nuevas
      formData.images.forEach((file) => {
        data.append('images', file);
      });

      // Si hay imágenes existentes (en caso de edición), enviarlas como JSON
      if (formData.existingImages && formData.existingImages.length > 0) {
        data.append('images', JSON.stringify(formData.existingImages));
      }

      if (selectedLog) {
        // Editar log existente
        await api.put(`/admin-logs/${selectedLog.id}`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        showSuccess('Log actualizado exitosamente');
      } else {
        // Crear nuevo log
        await api.post('/admin-logs', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        showSuccess('Log creado exitosamente');
      }
      
      setIsModalOpen(false);
      setSelectedLog(null);
      setFormData({
        change_type: 'Google',
        change_date: new Date().toISOString().split('T')[0],
        title: '',
        description: '',
        images: [],
        existingImages: []
      });
      loadLogs();
      loadStats();
    } catch (err: any) {
      showError(err.response?.data?.error || (selectedLog ? 'Error al actualizar log' : 'Error al crear log'));
    }
  };

  const handleEdit = (log: AdminLog) => {
    setSelectedLog(log);
    setFormData({
      change_type: log.change_type,
      change_date: log.change_date,
      title: log.title,
      description: log.description || '',
      images: [],
      existingImages: log.images || []
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (logId: number) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este log? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await api.delete(`/admin-logs/${logId}`);
      showSuccess('Log eliminado exitosamente');
      loadLogs();
      loadStats();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar log');
    }
  };

  const handleOpenModal = () => {
    setSelectedLog(null);
    setFormData({
      change_type: 'Google',
      change_date: new Date().toISOString().split('T')[0],
      title: '',
      description: '',
      images: [],
      existingImages: []
    });
    setIsModalOpen(true);
  };

  const toggleExpand = (logId: number) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  };

  const formatDateDisplay = (dateString: string) => {
    return formatDate(dateString);
  };

  // Preparar datos para el gráfico de línea por fecha
  // Ordenar por fecha ascendente para el gráfico de línea
  const chartDataByDate = statsByDate
    .map((item) => ({
      date: item.date,
      value: item.count,
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Preparar datos para el gráfico de pastel por tipo
  const chartDataByType = statsByType.map((item) => ({
    label: item.change_type,
    value: item.count,
    color: CHANGE_TYPE_COLORS[item.change_type as ChangeType] || CHANGE_TYPE_COLORS.Otros,
  }));

  // SISTEMA DE PERMISOS DESHABILITADO - Todos los usuarios tienen acceso

  // Obtener filtros activos para mostrar como chips
  const activeFilters = [];
  if (startDate) {
    const date = new Date(startDate);
    activeFilters.push(['start_date', date.toLocaleDateString('es-ES')]);
  }
  if (endDate) {
    const date = new Date(endDate);
    activeFilters.push(['end_date', date.toLocaleDateString('es-ES')]);
  }
  if (filterType && filterType !== 'all') {
    activeFilters.push(['type', filterType]);
  }

  const clearFilters = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    setStartDate(date.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setFilterType('all');
  };

  const getFilterLabel = (key: string, value: string) => {
    if (key === 'start_date') return `Desde: ${value}`;
    if (key === 'end_date') return `Hasta: ${value}`;
    if (key === 'type') return `Tipo: ${value}`;
    return null;
  };

  return (
    <div className="tab-content">
      {/* Header con título y botones */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ margin: 0, marginBottom: '0.75rem' }}>📋 Log de Administración</h2>
          </div>
          {/* Botones de acción */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              onClick={loadLogs}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <RefreshCw size={16} />
              Actualizar
            </button>
            <button className="btn btn-primary" onClick={handleOpenModal} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              ➕ Nuevo Log
            </button>
          </div>
        </div>

        {/* Filtros activos */}
        {activeFilters.length > 0 && (
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              FILTROS ACTIVOS:
            </span>
            {activeFilters.map(([key, value]) => {
              const label = getFilterLabel(key, value);
              if (!label) return null;
              return (
                <span
                  key={key}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.375rem 0.75rem',
                    background: '#fef3c7',
                    color: '#92400e',
                    borderRadius: '9999px',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  {label}
                  <button
                    onClick={() => {
                      if (key === 'start_date') {
                        const date = new Date();
                        date.setMonth(date.getMonth() - 1);
                        setStartDate(date.toISOString().split('T')[0]);
                      } else if (key === 'end_date') {
                        setEndDate(new Date().toISOString().split('T')[0]);
                      } else if (key === 'type') {
                        setFilterType('all');
                      }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      color: '#92400e'
                    }}
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}
            <button
              onClick={clearFilters}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent)',
                cursor: 'pointer',
                fontSize: '0.875rem',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              Limpiar todo
            </button>
          </div>
        )}

        {/* Filtros de búsqueda */}
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          marginBottom: '1rem'
        }}>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Fecha Inicio
            </label>
            <div style={{ position: 'relative' }}>
              <Calendar size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} />
              <input
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Fecha Fin
            </label>
            <div style={{ position: 'relative' }}>
              <Calendar size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} />
              <input
                type="date"
                className="input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
          </div>
          <div style={{ flex: '0 0 180px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Tipo
            </label>
            <select
              className="input"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as ChangeType | 'all')}
            >
              <option value="all">Todos</option>
              <option value="Google">Google</option>
              <option value="Meta">Meta</option>
              <option value="Klaviyo">Klaviyo</option>
              <option value="Fabricacion">Fabricación</option>
              <option value="Lanzamiento">Lanzamiento</option>
              <option value="Promo">Promo</option>
              <option value="Otros">Otros</option>
            </select>
          </div>
        </div>
      </div>

      {/* Gráficos Mejorados */}
      <div className="admin-log-charts">
        {/* Resumen de Métricas */}
        <div className="chart-card">
          <h3>📊 Resumen del Período</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
            <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--accent)' }}>
                {logs.length}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Total Eventos
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--info)' }}>
                {statsByType.length}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Tipos Diferentes
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success)' }}>
                {statsByType.length > 0 ? Math.max(...statsByType.map(s => s.count)) : 0}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Máx. por Tipo
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--warning)' }}>
                {statsByDate.length > 0 ? Math.max(...statsByDate.map(s => s.count)) : 0}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Máx. por Día
              </div>
            </div>
          </div>
        </div>

        {/* Distribución por Tipo con Detalles */}
        <div className="chart-card">
          <h3>📈 Distribución por Tipo</h3>
          {chartDataByType.length > 0 ? (
            <div>
              <PieChart data={chartDataByType} size={200} />
              <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {chartDataByType
                  .sort((a, b) => b.value - a.value)
                  .map((item) => {
                    const percentage = ((item.value / logs.length) * 100).toFixed(1);
                    return (
                      <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: item.color,
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{item.label}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                              {item.value} eventos
                            </span>
                            <span style={{ 
                              fontSize: '0.75rem', 
                              color: 'var(--text-secondary)',
                              background: 'var(--bg-tertiary)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontWeight: 600
                            }}>
                              {percentage}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
              No hay datos en el período seleccionado
            </p>
          )}
        </div>

        {/* Tendencias por Fecha con Mejoras */}
        <div className="chart-card" style={{ gridColumn: 'span 2' }}>
          <h3>📅 Tendencias Temporales</h3>
          {chartDataByDate.length > 0 ? (
            <div>
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  {chartDataByDate.length} días con actividad
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Promedio: {(chartDataByDate.reduce((sum, item) => sum + item.value, 0) / chartDataByDate.length).toFixed(1)} eventos/día
                </div>
              </div>
              <div style={{ padding: '1rem 0' }}>
                <LineChart
                  data={chartDataByDate.map((item) => ({
                    date: item.date,
                    value: item.value,
                  }))}
                  height={250}
                  lineColor="var(--accent)"
                  fillColor="rgba(0, 128, 96, 0.1)"
                  showPoints={true}
                />
              </div>
              {/* Estadísticas adicionales */}
              <div style={{ 
                marginTop: '1rem', 
                padding: '1rem', 
                background: 'var(--bg-tertiary)', 
                borderRadius: '8px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '1rem'
              }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    Día con más actividad
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    {chartDataByDate.length > 0 
                      ? formatDateDisplay(chartDataByDate.reduce((max, item) => item.value > max.value ? item : max, chartDataByDate[0]).date)
                      : '-'
                    }
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {chartDataByDate.length > 0 
                      ? `${chartDataByDate.reduce((max, item) => item.value > max.value ? item : max, chartDataByDate[0]).value} eventos`
                      : ''
                    }
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    Total en período
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    {chartDataByDate.reduce((sum, item) => sum + item.value, 0)} eventos
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    Tendencia
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    {chartDataByDate.length >= 2 
                      ? (() => {
                          const first = chartDataByDate[0].value;
                          const last = chartDataByDate[chartDataByDate.length - 1].value;
                          const diff = last - first;
                          const trend = diff > 0 ? '📈 Creciente' : diff < 0 ? '📉 Decreciente' : '➡️ Estable';
                          return trend;
                        })()
                      : '➡️ Estable'
                    }
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
              No hay datos en el período seleccionado
            </p>
          )}
        </div>
      </div>

      {/* Tabla de Logs */}
      {loading ? (
        <div className="loading-state">
          <p>Cargando logs...</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>FECHA</th>
                  <th>TÍTULO</th>
                  <th>TIPO</th>
                  <th>DESCRIPCIÓN</th>
                  <th>USUARIO</th>
                  {isAdmin && <th>ACCIONES</th>}
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 6 : 5} className="text-center p-0">
                      <div className="empty-state">
                        <p>No hay logs en el período seleccionado</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const isExpanded = expandedLogs.has(log.id);
                    return (
                      <>
                        <tr
                          key={log.id}
                          style={{
                            cursor: 'pointer',
                            backgroundColor: isExpanded ? 'var(--bg-secondary)' : 'transparent'
                          }}
                          onClick={() => toggleExpand(log.id)}
                        >
                          <td style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                            {formatDateDisplay(log.change_date)}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ display: 'flex', alignItems: 'center' }}>
                                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              </span>
                              <span style={{ fontWeight: 600 }}>{log.title}</span>
                            </div>
                          </td>
                          <td>
                            <span
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                backgroundColor: CHANGE_TYPE_COLORS[log.change_type] + '20',
                                color: CHANGE_TYPE_COLORS[log.change_type],
                                border: `1px solid ${CHANGE_TYPE_COLORS[log.change_type]}40`
                              }}
                            >
                              {log.change_type}
                            </span>
                          </td>
                          <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            {log.description ? (log.description.length > 50 ? log.description.substring(0, 50) + '...' : log.description) : '-'}
                          </td>
                          <td style={{ fontSize: '0.875rem' }}>{log.username || 'Sistema'}</td>
                          {isAdmin && (
                            <td>
                              <div style={{ display: 'flex', gap: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                                <button
                                  className="btn btn-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(log);
                                  }}
                                  style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                                >
                                  Editar
                                </button>
                                <button
                                  className="btn btn-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(log.id);
                                  }}
                                  style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                                >
                                  Eliminar
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                        {isExpanded && (
                          <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
                            <td colSpan={isAdmin ? 6 : 5} style={{ padding: '1rem', paddingLeft: '3rem' }}>
                              {log.description && (
                                <div style={{ marginBottom: '1rem' }}>
                                  <strong style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                                    Descripción/Motivo:
                                  </strong>
                                  <p style={{ margin: 0, color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                    {log.description}
                                  </p>
                                </div>
                              )}
                              
                              {/* Mostrar imágenes adjuntas */}
                              {log.images && log.images.length > 0 && (
                                <div style={{ 
                                  marginTop: '1rem', 
                                  display: 'flex', 
                                  gap: '0.5rem', 
                                  flexWrap: 'wrap' 
                                }}>
                                  {log.images.map((url, idx) => (
                                    <div
                                      key={idx} 
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedImage(url);
                                      }}
                                      style={{
                                        display: 'block',
                                        width: '120px',
                                        height: '120px',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s, box-shadow 0.2s'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.boxShadow = 'none';
                                      }}
                                    >
                                      <img 
                                        src={url} 
                                        alt={`Adjunto ${idx + 1}`} 
                                        style={{ 
                                          width: '100%', 
                                          height: '100%', 
                                          objectFit: 'cover' 
                                        }} 
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              <div style={{ 
                                marginTop: '1rem',
                                paddingTop: '1rem',
                                borderTop: '1px solid var(--border-color)',
                                fontSize: '0.75rem',
                                color: 'var(--text-secondary)'
                              }}>
                                Creado: {formatDateDisplay(log.created_at)}
                                {log.username && ` por ${log.username}`}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal para crear nuevo log */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedLog(null);
        }}
        title={selectedLog ? 'Editar Log' : 'Nuevo Log'}
        size="medium"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedLog(null);
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              data-modal-default="true"
            >
              {selectedLog ? 'Guardar Cambios' : 'Crear'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Tipo de Cambio *</label>
              <select
                className="input"
                value={formData.change_type}
                onChange={(e) =>
                  setFormData({ ...formData, change_type: e.target.value as ChangeType })
                }
                required
              >
                <option value="Google">Google</option>
                <option value="Meta">Meta</option>
                <option value="Klaviyo">Klaviyo</option>
                <option value="Fabricacion">Fabricación</option>
                <option value="Lanzamiento">Lanzamiento</option>
                <option value="Promo">Promo</option>
                <option value="Otros">Otros</option>
              </select>
            </div>

            <div className="input-group">
              <label className="input-label">Fecha del Cambio *</label>
              <input
                type="date"
                className="input"
                value={formData.change_date}
                onChange={(e) =>
                  setFormData({ ...formData, change_date: e.target.value })
                }
                required
              />
            </div>

            <div className="input-group" style={{ gridColumn: '1 / -1' }}>
              <label className="input-label">Título *</label>
              <input
                type="text"
                className="input"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Ej: Ajuste en ROAS de Google PMAX"
                required
              />
            </div>

            <div className="input-group" style={{ gridColumn: '1 / -1' }}>
              <label className="input-label">Descripción/Motivo</label>
              <textarea
                className="input"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                onPaste={handlePaste}
                placeholder="Describe el cambio realizado y el motivo... (Puedes pegar imágenes aquí con Ctrl+V)"
                rows={4}
              />
            </div>

            {/* Área de Imágenes */}
            <div className="input-group" style={{ gridColumn: '1 / -1' }}>
              <label className="input-label">
                Imágenes Adjuntas (Máx 10) - {formData.images.length + (formData.existingImages?.length || 0)}/10
              </label>
              <div 
                className="drop-zone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => document.getElementById('image-upload')?.click()}
                style={{
                  border: '2px dashed var(--border-color)',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  marginBottom: '1rem',
                  backgroundColor: 'var(--bg-secondary)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--accent)';
                  e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                }}
              >
                <p style={{ margin: '0.5rem 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  📎 Arrastra imágenes aquí, pégalas en la descripción o haz clic para seleccionar
                </p>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                  Formatos: JPG, PNG, GIF, WebP (máx 5MB por imagen)
                </p>
                <input
                  type="file"
                  id="image-upload"
                  multiple
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
              </div>

              {/* Previsualización de Imágenes */}
              {(formData.existingImages && formData.existingImages.length > 0) || formData.images.length > 0 ? (
                <div className="image-previews" style={{ 
                  display: 'flex', 
                  gap: '0.5rem', 
                  flexWrap: 'wrap',
                  marginTop: '0.5rem'
                }}>
                  {/* Imágenes Existentes */}
                  {formData.existingImages?.map((url, index) => (
                    <div key={`existing-${index}`} style={{ 
                      position: 'relative', 
                      width: '100px', 
                      height: '100px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={url} 
                        alt={`Adjunto ${index + 1}`} 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover' 
                        }} 
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index, true)}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          background: 'rgba(255, 0, 0, 0.8)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                          fontWeight: 'bold'
                        }}
                        title="Eliminar imagen"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  
                  {/* Nuevas Imágenes */}
                  {formData.images.map((file, index) => (
                    <div key={`new-${index}`} style={{ 
                      position: 'relative', 
                      width: '100px', 
                      height: '100px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt={`Nuevo ${index + 1}`} 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover' 
                        }} 
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index, false)}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          background: 'rgba(255, 0, 0, 0.8)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                          fontWeight: 'bold'
                        }}
                        title="Eliminar imagen"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </form>
      </Modal>

      {/* Modal para mostrar imagen en tamaño completo */}
      {selectedImage && (
        <Modal
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          title="Imagen adjunta"
          size="fullscreen"
        >
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            minHeight: 'calc(98vh - 150px)',
            overflow: 'auto',
            padding: '2rem',
            backgroundColor: 'var(--bg-secondary)'
          }}>
            <img 
              src={selectedImage} 
              alt="Imagen adjunta" 
              style={{ 
                width: 'auto',
                height: 'auto',
                maxWidth: '95%',
                maxHeight: 'calc(98vh - 200px)',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                cursor: 'zoom-in',
                transition: 'transform 0.3s ease'
              }} 
              onClick={(e) => {
                // Permitir zoom al hacer clic
                const img = e.currentTarget;
                if (img.style.transform === 'scale(1.5)') {
                  img.style.transform = 'scale(1)';
                  img.style.cursor = 'zoom-in';
                } else {
                  img.style.transform = 'scale(1.5)';
                  img.style.cursor = 'zoom-out';
                }
              }}
            />
          </div>
          <div style={{ 
            marginTop: '1rem', 
            paddingTop: '1rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '0.5rem' 
          }}>
            <button
              type="button"
              onClick={() => {
                const link = document.createElement('a');
                link.href = selectedImage;
                link.download = `imagen-${Date.now()}.png`;
                link.click();
              }}
              className="btn btn-secondary"
            >
              Descargar
            </button>
            <button
              type="button"
              onClick={() => setSelectedImage(null)}
              className="btn btn-primary"
            >
              Cerrar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

